// The rules of the game, for players to read: from the main menu, the 1v1 and 2v2 menus and the table.
// The words come straight from data/rules.txt (see the note at the top of that file for its few marks).
import rulesText from '../../data/rules.txt';
import { el, button, modal } from './ui.ts';

type Block = { kind: 'p' | 'h'; text: string } | { kind: 'ul'; items: { text: string; sub: string[] }[] };
interface Section {
  title: string;
  chapter: string;
  blocks: Block[];
}

function parse(text: string): Section[] {
  const sections: Section[] = [];
  let chapter = '';
  let cur: Section | null = null;
  let para: string[] = [];
  const flush = () => {
    if (cur && para.length) cur.blocks.push({ kind: 'p', text: para.join(' ') });
    para = [];
  };
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    const t = line.trim();
    if (t.startsWith('//')) continue;
    if (!t) {
      flush();
      continue;
    }
    if (/^=\s+/.test(t)) {
      flush();
      chapter = t.replace(/^=\s+/, '');
      continue;
    }
    if (/^##\s+/.test(t) && !/^###/.test(t)) {
      flush();
      cur = { title: t.replace(/^##\s+/, ''), chapter, blocks: [] };
      sections.push(cur);
      continue;
    }
    if (!cur) continue;
    if (/^###\s+/.test(t)) {
      flush();
      cur.blocks.push({ kind: 'h', text: t.replace(/^###\s+/, '') });
      continue;
    }
    const sub = /^--\s+/.test(t);
    if (sub || /^[-•]\s+/.test(t)) {
      flush();
      let list = cur.blocks.at(-1);
      if (!list || list.kind !== 'ul') {
        list = { kind: 'ul', items: [] };
        cur.blocks.push(list);
      }
      const item = t.replace(/^(--|[-•])\s+/, '');
      if (sub && list.items.length) list.items.at(-1)!.sub.push(item);
      else list.items.push({ text: item, sub: [] });
      continue;
    }
    para.push(t);
  }
  flush();
  return sections;
}

const SECTIONS = parse(rulesText);
export const hasRules = SECTIONS.length > 0;

/** A line of text, with **bold** turned into bold. */
function inline(text: string): (Node | string)[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part) => (/^\*\*[^*]+\*\*$/.test(part) ? el('strong', {}, part.slice(2, -2)) : part));
}

function sectionText(s: Section) {
  return [s.title, ...s.blocks.map((b) => (b.kind === 'ul' ? b.items.map((i) => [i.text, ...i.sub].join(' ')).join(' ') : b.text))].join(' ').toLowerCase();
}

/**
 * The section about a token, if the rules have one: by its name ("Bleed"), or by its effect
 * ("damage-boost" → Damage Boost, "locked" → Locked). null if there is none.
 */
export function ruleFor(...names: (string | undefined)[]): string | null {
  for (const n of names) {
    if (!n) continue;
    const want = n.toLowerCase().replace(/[-_]+/g, ' ').trim();
    const hit = SECTIONS.find((s) => s.title.toLowerCase() === want) ?? SECTIONS.find((s) => s.chapter === 'Status Effects' && want.startsWith(s.title.toLowerCase()));
    if (hit) return hit.title;
  }
  return null;
}

/**
 * Opens the rules: a list of sections on the left (by chapter, with a search box), the text on the right.
 * `open` jumps straight to a section by its title ("Bleed").
 */
export function showRules(open?: string) {
  const content = el('div', { class: 'rules-content' });
  const nav = el('nav', { class: 'rules-nav' });
  const search = el('input', { class: 'text-input rules-search', type: 'search', placeholder: 'Search the rules…', 'aria-label': 'Search the rules' }) as HTMLInputElement;
  const links = new Map<Section, HTMLElement>();
  const bodies = new Map<Section, HTMLElement>();

  const show = (s: Section) => {
    for (const [sec, link] of links) link.classList.toggle('active', sec === s);
    const node = bodies.get(s);
    node?.scrollIntoView({ block: 'start' });
  };

  let chapter = '';
  for (const s of SECTIONS) {
    if (s.chapter !== chapter) {
      chapter = s.chapter;
      if (chapter) nav.append(el('div', { class: 'rules-chapter' }, chapter));
      content.append(el('h2', { class: 'rules-chapter-title' }, chapter || 'Rules'));
    }
    const link = el('button', { class: 'rules-link', type: 'button', onclick: () => show(s) }, s.title);
    links.set(s, link);
    nav.append(link);
    const body = el(
      'section',
      { class: 'rules-section' },
      el('h3', {}, s.title),
      ...s.blocks.map((b) => {
        if (b.kind === 'ul') return el('ul', {}, ...b.items.map((i) => el('li', {}, ...inline(i.text), i.sub.length ? el('ul', {}, ...i.sub.map((x) => el('li', {}, ...inline(x)))) : '')));
        return b.kind === 'h' ? el('h4', {}, ...inline(b.text)) : el('p', {}, ...inline(b.text));
      }),
    );
    bodies.set(s, body);
    content.append(body);
  }

  // the search box: only the sections that mention every word typed stay, in the list and in the text
  const texts = new Map(SECTIONS.map((s) => [s, sectionText(s)]));
  const none = el('p', { class: 'muted rules-none', hidden: true }, 'Nothing in the rules matches that.');
  content.append(none);
  search.addEventListener('input', () => {
    const words = search.value.toLowerCase().split(/\s+/).filter(Boolean);
    let any = false;
    for (const s of SECTIONS) {
      const hit = words.every((w) => texts.get(s)!.includes(w));
      links.get(s)!.hidden = !hit;
      bodies.get(s)!.hidden = !hit;
      any ||= hit;
    }
    content.querySelectorAll<HTMLElement>('.rules-chapter-title').forEach((h) => (h.hidden = words.length > 0));
    nav.querySelectorAll<HTMLElement>('.rules-chapter').forEach((h) => (h.hidden = words.length > 0));
    none.hidden = any;
    content.scrollTop = 0;
  });

  // the link of whichever section is being read lights up as the text scrolls
  content.addEventListener('scroll', () => {
    const top = content.getBoundingClientRect().top + 8;
    let current: Section | null = null;
    for (const [s, node] of bodies) {
      if (node.hidden) continue;
      if (node.getBoundingClientRect().top <= top) current = s;
    }
    for (const [s, link] of links) link.classList.toggle('active', s === current);
  });

  let close = () => {};
  close = modal(
    el(
      'div',
      { class: 'rules-modal-inner' },
      el('div', { class: 'rules-head' }, el('h2', {}, 'Rules'), search, button('Close', () => close(), 'btn-ghost')),
      el('div', { class: 'rules-body' }, nav, content),
    ),
    { wide: true, className: 'rules-modal' },
  );
  const target = open ? SECTIONS.find((s) => s.title.toLowerCase() === open.toLowerCase()) : undefined;
  requestAnimationFrame(() => (target ? show(target) : links.get(SECTIONS[0])?.classList.add('active')));
}
